import {
  addDays,
  differenceInSeconds,
  differenceInDays,
  addSeconds,
  isAfter
} from 'date-fns';
import GoogleFit, { Scopes } from 'react-native-google-fit';
import api from '../api/api';
import {
  UserAttribute,
  UserAttributeSource,
  UserAttributeType
} from '../models/UserAttribute';
import AsyncStorage from '@react-native-async-storage/async-storage';
import environment from '../environments';
import { log } from './LogHelper';
import { stringify } from 'flatted';
import { Platform, PermissionsAndroid, Alert, Linking } from 'react-native';

const options = {
  scopes: environment.googleFitScopes as Scopes[]
};

export class GoogleFitHealth {
  userId: number;
  requestInProgress = false;

  constructor(userId: number) {
    this.userId = userId;
  }

  disconnect() {
    return GoogleFit.disconnect();
  }

  async authorize() {
    await GoogleFit.checkIsAuthorized();
    if (GoogleFit.isAuthorized) {
      AsyncStorage.setItem('google-fit-connected', 'true');
      return true;
    } else {
      try {
        const authResult = await GoogleFit.authorize(options);
        if (authResult.success) {
          await AsyncStorage.setItem('google-fit-connected', 'true');
        } else {
          await AsyncStorage.removeItem('google-fit-connected');
        }
        return authResult.success;
      } catch (error) {
        log(stringify(error));
        await AsyncStorage.removeItem('google-fit-connected');
        return false;
      }
    }
  }

  async getIsGoogleFitAuthorized() {
    let isAuthorized = false;
    try {
      await GoogleFit.checkIsAuthorized();
      isAuthorized =
        GoogleFit.isAuthorized ||
        Boolean(await AsyncStorage.getItem('google-fit-connected'));
      return isAuthorized;
    } catch (error) {
      log(`getIsGoogleFitAuthorized error:`, stringify(error));
    }
    return isAuthorized;
  }

  async getLastSync(source: UserAttributeSource) {
    try {
      const result = await api.get(
        `users/${this.userId}/attributes/last?source=${source}`,
        {}
      );
      const response = await result.response;
      const lastSync: UserAttribute = await response.json();
      return lastSync;
    } catch (error) {
      log(stringify(error));
      return undefined;
    }
  }

  async getDataToSynchronize(progress: (message: string) => void) {
    await this.authorize();
    const today = new Date();
    const lastSync = await this.getLastSync(UserAttributeSource.GoogleFit);
    let startDate =
      lastSync && differenceInDays(today, new Date(lastSync.startDate)) < 90
        ? new Date(lastSync.startDate)
        : addDays(new Date(), -90);

    const data: UserAttribute[] = [];

    while (startDate < today) {
      const chunkEndDate = addDays(startDate, 30);
      const endDate = chunkEndDate > today ? today : chunkEndDate;

      const sleepData = await this.getSleepData(
        addSeconds(startDate, 1),
        endDate
      );
      if (sleepData && sleepData.length > 0) {
        data.push(...sleepData);
        progress(`Sleep (${sleepData.length})`);
      }

      const heartRateData = await this.getHeartRateData(
        addSeconds(startDate, 1),
        endDate
      );
      if (heartRateData && heartRateData.length > 0) {
        data.push(...heartRateData);
        progress(`Heart Rate (${heartRateData.length})`);
      }

      const oxygenSaturationData = await this.getOxygenSaturationData(
        addSeconds(startDate, 1),
        endDate
      );
      if (oxygenSaturationData && oxygenSaturationData.length > 0) {
        data.push(...oxygenSaturationData);
        progress(`Oxygen Saturation (${oxygenSaturationData.length})`);
      }

      const stepCountData = await this.getStepCountData(
        addSeconds(startDate, 1),
        endDate
      );
      if (stepCountData && stepCountData.length > 0) {
        data.push(...stepCountData);
        progress(`Steps (${stepCountData.length})`);
      }

      startDate = endDate;
      if (startDate < endDate) {
        // Add delay to throttle API call when syncing more than 30 days of data
        await new Promise(res => setTimeout(res, 5000));
      }
    }
    return data;
  }

  async synchronizeData() {
    await this.authorize();
    const lastSync = await this.getLastSync(UserAttributeSource.GoogleFit);
    let data: any[] = [];
    const startDate =
      lastSync &&
      differenceInDays(new Date(), new Date(lastSync.startDate)) < 30
        ? new Date(lastSync.startDate)
        : addDays(new Date(), -30);

    const sleepData = await this.getSleepData(addSeconds(startDate, 1));
    if (sleepData && sleepData.length > 0) {
      data = [...data, ...sleepData];
    }

    const heartRateData = await this.getHeartRateData(startDate);
    if (heartRateData && heartRateData.length > 0) {
      data = [...data, ...heartRateData];
    }

    const oxygenSaturationData = await this.getOxygenSaturationData(startDate);
    if (oxygenSaturationData && oxygenSaturationData.length > 0) {
      data = [...data, ...oxygenSaturationData];
    }

    const bloodPressureData = await this.getBloodPressureData(startDate);
    if (bloodPressureData && bloodPressureData.length > 0) {
      data = [...data, ...bloodPressureData];
    }

    const bloodGlucoseData = await this.getBloodGlucoseData(startDate);
    if (bloodGlucoseData && bloodGlucoseData.length > 0) {
      data = [...data, ...bloodGlucoseData];
    }

    const bodyTemperatureData = await this.getBodyTemperatureData(startDate);
    if (bodyTemperatureData && bodyTemperatureData.length > 0) {
      data = [...data, ...bodyTemperatureData];
    }

    const stepData = await this.getStepCountData(startDate);
    if (stepData && stepData.length > 0) {
      data = [...data, ...stepData];
    }

    const caloriesConsumedData = await this.getCaloriesConsumedData(startDate);
    if (caloriesConsumedData && caloriesConsumedData.length > 0) {
      data = [...data, ...caloriesConsumedData];
    }

    const caloriesBurnedData = await this.getCaloriesBurnedData(startDate);
    if (caloriesBurnedData && caloriesBurnedData.length > 0) {
      data = [...data, ...caloriesBurnedData];
    }

    const weightData = await this.getWeightData(startDate);
    if (weightData && weightData.length > 0) {
      data = [...data, ...weightData];
    }

    // send data array to server
    if (data.length > 0) {
      await api
        .post(`users/${this.userId}/attributes`, this.sortData(data, 'ASC'))
        .response.catch(error => {
          log(stringify(error));
        });
    }
  }

  private sortData(data: UserAttribute[], order: 'ASC' | 'DESC') {
    return data.sort((a, b) =>
      order === 'DESC'
        ? b.startDate.getTime() - a.startDate.getTime()
        : a.startDate.getTime() - b.startDate.getTime()
    );
  }

  private async getSleepData(
    startDate: Date,
    endDate?: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getSleepSamples(
        {
          startDate: startDate.toISOString(),
          endDate: (endDate || new Date()).toISOString()
        },
        true
      );

      return result.map(
        data =>
          ({
            userId: this.userId,
            type: UserAttributeType.Sleep,
            numberValue:
              differenceInSeconds(
                new Date(data.endDate),
                new Date(data.startDate)
              ) /
              60 /
              60,
            unit: 'h',
            source: UserAttributeSource.GoogleFit,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
            jsonValue: data,
            subSource: data.addedBy
          }) as UserAttribute
      );
    } catch (error) {
      log(`Error: ${error}`);
    }
  }

  private async getBloodPressureData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getBloodPressureSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });
      return result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.BloodPressure,
        textValue: `${data.systolic}/${data.diastolic}`,
        unit: 'mmHg',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        jsonValue: data
      }));
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }
  private async getStepCountData(startDate: Date, endDate?: Date) {
    const result: UserAttribute[] = [];
    try {
      const results = await GoogleFit.getDailyStepCountSamples({
        startDate: startDate.toISOString(),
        endDate: (endDate || new Date()).toISOString()
      });
      results
        ?.filter(
          result => result.source === 'com.google.android.gms:estimated_steps'
        )
        .forEach(data => {
          data.steps.forEach(step => {
            if (isAfter(new Date(step.date), startDate))
              result.push({
                userId: this.userId,
                type: UserAttributeType.Steps,
                numberValue: step.value,
                source: UserAttributeSource.GoogleFit,
                startDate: new Date(step.date),
                endDate: new Date(step.date),
                jsonValue: step,
                subSource: 'none'
              } as UserAttribute);
          });
        });
    } catch (error) {
      log(`Error: ${error}`);
    }
    return result;
  }

  private async getWeightData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getWeightSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });

      return result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.Weight,
        numberValue: data.value,
        unit: 'kg',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        jsonValue: data
      }));
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }

  private async getBodyTemperatureData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getBodyTemperatureSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });
      result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.BodyTemperature,
        numberValue: data.value,
        unit: 'calories',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        jsonValue: data
      }));
      return;
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }

  private async getHeartRateData(
    startDate: Date,
    endDate?: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getHeartRateSamples({
        startDate: startDate.toISOString(),
        endDate: (endDate || new Date()).toISOString()
      });
      return result.map(
        data =>
          ({
            userId: this.userId,
            type: UserAttributeType.HeartRate,
            numberValue: data.value,
            unit: 'bpm',
            source: UserAttributeSource.GoogleFit,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
            jsonValue: data,
            subSource: (data as any).addedBy || 'none'
          }) as UserAttribute
      );
    } catch (error) {
      log(`Error: ${error}`);
    }
  }

  private async getBloodGlucoseData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getBloodGlucoseSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });
      return result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.BloodGlucose,
        numberValue: data.value,
        unit: 'mmol/L',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        jsonValue: data
      }));
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }

  private async getOxygenSaturationData(
    startDate: Date,
    endDate?: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getOxygenSaturationSamples({
        startDate: startDate.toISOString(),
        endDate: (endDate || new Date()).toISOString()
      });

      return result.map(
        data =>
          ({
            userId: this.userId,
            type: UserAttributeType.OxygenSaturation,
            numberValue: data.value,
            unit: '%',
            source: UserAttributeSource.GoogleFit,
            startDate: new Date(data.startDate),
            endDate: new Date(data.endDate),
            jsonValue: data
          }) as UserAttribute
      );
    } catch (error) {
      log(`Error: ${error}`);
    }
  }

  private async getCaloriesConsumedData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getDailyNutritionSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });
      return result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.CaloriesConsumed,
        numberValue: data.nutrients.calories,
        unit: 'cal',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.date),
        endDate: new Date(data.date),
        jsonValue: data
      }));
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }

  private async getCaloriesBurnedData(
    startDate: Date
  ): Promise<UserAttribute[] | undefined> {
    try {
      const result = await GoogleFit.getDailyCalorieSamples({
        startDate: startDate.toISOString(),
        endDate: new Date().toISOString()
      });
      return result.map(data => ({
        userId: this.userId,
        type: UserAttributeType.CaloriesBurned,
        numberValue: data.calorie,
        unit: 'cal',
        source: UserAttributeSource.GoogleFit,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        jsonValue: data
      }));
    } catch (error) {
      console.log(`Error: ${error}`);
    }
  }
}

export async function checkPhysicalActivityPermission() {
  if (Platform.OS === 'android') {
    const requestPermission = async () => {
      return await PermissionsAndroid.request(
        'android.permission.ACTIVITY_RECOGNITION',
        {
          title: 'Permission Required',
          message:
            'In order to access your data from Google Fit, please allow "Physical Activity" permission.',
          buttonPositive: 'Ok'
        }
      );
    };
    let result = await requestPermission();
    // request again if denied
    if (result === 'denied') {
      result = await requestPermission();
    }
    // no need for denied as declining twice will result in never_ask_again
    return result === 'never_ask_again' ? 'never_ask_again' : 'granted';
  }
  return 'unsupported';
}

export function showPhysicalActivityAlert(): Promise<
  'cancel' | 'disconnect' | 'settings'
> {
  return new Promise(resolve => {
    Alert.alert(
      'Permission Required',
      'In order to access your data from Google Fit, please enable "Physical Activity" permission in your app settings.',
      [
        {
          text: 'Close',
          style: 'cancel',
          onPress: () => resolve('cancel')
        },
        {
          text: 'Disconnect',
          onPress: async () => {
            await AsyncStorage.removeItem('google-fit-connected');
            GoogleFit.disconnect();
            resolve('disconnect');
          }
        },
        {
          text: 'Settings',
          onPress: async () => {
            await Linking.openSettings();
            resolve('settings');
          }
        }
      ],
      {
        cancelable: false
      }
    );
  });
}
